import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { ArrowRight, CheckCircle2, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [showPw, setShowPw] = useState(false);

  const strength = useMemo(() => {
    let s = 0;
    if (pw.length >= 8) s++;
    if (/[A-Z]/.test(pw)) s++;
    if (/[0-9]/.test(pw)) s++;
    if (/[^A-Za-z0-9]/.test(pw)) s++;
    return s; // 0..4
  }, [pw]);
  const strengthLabels = ["Te kort", "Zwak", "Redelijk", "Sterk", "Zeer sterk"];
  const strengthColors = ["bg-red-500", "bg-red-400", "bg-amber-400", "bg-emerald-400", "bg-emerald-500"];

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw !== pw2) { setErr("Wachtwoorden komen niet overeen"); return; }
    if (pw.length < 8) { setErr("Minimaal 8 tekens"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Wachtwoord bijgewerkt");
      setTimeout(() => nav("/login"), 2000);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen grid place-items-center bg-white px-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#1B2D5C] mb-2">Ongeldige link</h1>
          <p className="text-sm text-zinc-500 mb-4">Vraag een nieuwe link aan om je wachtwoord te resetten.</p>
          <Link to="/wachtwoord-vergeten" className="text-[#5BC044] hover:underline">Nieuwe link aanvragen →</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center bg-white px-6" data-testid="reset-password-page">
      <div className="w-full max-w-sm">
        <img src="/stockly-logo-tight.png" alt="Stockly" className="w-40 h-auto mb-8" />
        <Link to="/login" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-[#1B2D5C] mb-6">
          <ArrowLeft size={14} /> Terug naar inloggen
        </Link>

        {!done ? (
          <>
            <h1 className="text-3xl font-bold tracking-tight mb-2 text-[#1B2D5C]">Nieuw wachtwoord</h1>
            <p className="text-sm text-zinc-500 mb-6">Kies een sterk wachtwoord (min. 8 tekens, 1 hoofdletter, 1 cijfer).</p>
            <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-form">
              <div>
                <label className="overline block mb-2">Nieuw wachtwoord</label>
                <div className="relative">
                  <input
                    type={showPw ? "text" : "password"} required value={pw} minLength={8}
                    onChange={(e) => setPw(e.target.value)}
                    className="w-full h-11 pl-3 pr-11 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#5BC044]/45 focus:border-[#5BC044]"
                    data-testid="reset-pw-input"
                  />
                  <button type="button" onClick={() => setShowPw((v) => !v)} tabIndex={-1}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 inline-flex items-center justify-center text-zinc-400 hover:text-[#1B2D5C] rounded"
                    aria-label={showPw ? "Wachtwoord verbergen" : "Wachtwoord tonen"}
                    data-testid="reset-toggle-pw"
                  >
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {pw && (
                  <div className="mt-2">
                    <div className="h-1.5 w-full bg-zinc-100 rounded overflow-hidden flex gap-0.5">
                      {[0,1,2,3].map((i) => (
                        <div key={i} className={`flex-1 ${i < strength ? strengthColors[Math.max(0, strength-1)] : "bg-zinc-200"}`} />
                      ))}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">{strengthLabels[Math.min(strength, 4)]}</div>
                  </div>
                )}
              </div>
              <div>
                <label className="overline block mb-2">Herhaal wachtwoord</label>
                <input
                  type={showPw ? "text" : "password"} required value={pw2} minLength={8}
                  onChange={(e) => setPw2(e.target.value)}
                  className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#5BC044]/45 focus:border-[#5BC044]"
                  data-testid="reset-pw2-input"
                />
              </div>
              {err && <div className="text-sm text-red-600" data-testid="reset-error">{err}</div>}
              <button type="submit" disabled={loading || pw.length < 8 || pw !== pw2}
                className="w-full h-11 bg-[#5BC044] hover:bg-[#4FAA3A] disabled:opacity-60 text-white rounded-md font-semibold inline-flex items-center justify-center gap-2"
                data-testid="reset-submit-button"
              >
                {loading ? "Bezig…" : "Wachtwoord opslaan"} <ArrowRight size={16} />
              </button>
            </form>
          </>
        ) : (
          <div className="text-center" data-testid="reset-done-state">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-4">
              <CheckCircle2 size={28} className="text-[#5BC044]" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight mb-2 text-[#1B2D5C]">Klaar!</h1>
            <p className="text-sm text-zinc-500">Je wordt zo doorgestuurd naar inloggen…</p>
          </div>
        )}
      </div>
    </div>
  );
}
